import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Button,
  message,
  Popconfirm,
  Select,
  Space,
} from "antd";
import { BiEdit } from "react-icons/bi";
import { AiFillDelete, AiOutlineUpload } from "react-icons/ai";
import { FileExcelOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";


const Productlist = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [sizePricePairs, setSizePricePairs] = useState([]);
  const [productProperties, setProductProperties] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [currentReview, setCurrentReview] = useState(null);
  const [isEditingReview, setIsEditingReview] = useState(false);
  const [reviewForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [attributeNames, setAttributeNames] = useState([]); // For attribute names
  const [attributeValues, setAttributeValues] = useState({}); // For attribute values
  const [totalQuantity, setTotalQuantity] = useState(0); // Add this line to track total quantity
  const [exportLoading, setExportLoading] = useState(false);

  // Filter the products based on search
  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchAllAttributes();
  }, []);

  // Add this useEffect to update form when sizePricePairs changes
  useEffect(() => {
    // Calculate total quantity from size-price pairs
    const newTotalQuantity = calculateTotalQuantity();
    setTotalQuantity(newTotalQuantity);
    form.setFieldsValue({ quantity: newTotalQuantity });
  }, [sizePricePairs]);

  const calculateTotalQuantity = () => {
    return sizePricePairs.reduce(
      (total, pair) => total + (parseInt(pair.quantity) || 0),
      0
    );
  };

  const fetchAllAttributes = async () => {
    try {
      const response = await axios.get(
        "https://backend.gulabidecor.com/api/attributes/getAllAttributes"
      );
      setAttributeNames(response.data);
    } catch (err) {
      console.error("Error fetching attributes:", err.message);
    }
  };

  const fetchAttributeValues = async (attributeId, index) => {
    try {
      const response = await axios.get(
        `https://backend.gulabidecor.com/api/attributes/values/${attributeId}`
      );
      setAttributeValues((prevValues) => ({
        ...prevValues,
        [index]: response.data.map((item) => item.value),
      }));
    } catch (err) {
      console.error("Error fetching attribute values:", err.message);
    }
  };

  const handleAttributeKeyChange = (index, value) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index].key = value;
    updatedAttributes[index].value = ""; // Reset value when key changes
    setAttributes(updatedAttributes);

    const selectedAttribute = attributeNames.find((attr) => attr.name === value);
    if (selectedAttribute) {
      fetchAttributeValues(selectedAttribute.attribute_id, index);
    }
  };

  const handleCustomValueChange = (index, value) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index].value = value; // Update the custom value
    setAttributes(updatedAttributes);
  };

  const handleCustomValueToggle = (index, enableCustom) => {
    const updatedAttributes = [...attributes];
    if (enableCustom) {
      updatedAttributes[index].isCustom = true; // Enable custom input
      updatedAttributes[index].value = ""; // Clear the value for custom input
    } else {
      updatedAttributes[index].isCustom = false; // Disable custom input
      updatedAttributes[index].value = ""; // Reset value to empty
    }
    setAttributes(updatedAttributes);
  };

  // Update the `handleAttributeValueChange` function to handle the "__custom" case
  const handleAttributeValueChange = (index, value) => {
    const updatedAttributes = [...attributes];
    if (value === "__custom") {
      updatedAttributes[index].value = ""; // Set value to empty for custom input
    } else {
      updatedAttributes[index].value = value; // Set the selected dropdown value
    }
    setAttributes(updatedAttributes);
  };

  const navigate = useNavigate();

  const adminToken = localStorage.getItem("adminToken");
  console.log("Admin Token:", adminToken);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      navigate("/");
    }
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        "https://backend.gulabidecor.com/api/products/getAllProducts"
      );

      const formattedData = response.data.result.map((product) => {
        // Calculate total quantity from size-price pairs
        const sizePricePairs = getUniqueSizePricePairs(product.size_price_pairs);
        const calculatedQuantity = sizePricePairs.reduce(
          (total, pair) => total + (parseInt(pair.quantity) || 0),
          0
        );

        return {
          key: product.product_id,
          name: product.product_name,
          description: product.product_description,
          price: product.price,
          discount: product.discount,
          quantity: calculatedQuantity, // Use calculated quantity instead of database quantity
          category: product.category_name,
          attributes: getUniqueAttributes(product.product_attributes),
          sizePricePairs: sizePricePairs,
          productProperties: product.product_properties || {},
          images: product.product_images ? product.product_images.split(",") : [],
          category_names_array: product.category_names_array,
        };
      });

      setProducts(formattedData);
    } catch (err) {
      console.error("Error fetching products:", err.message);
    }
  };

  // Helper function to get unique attributes
  const getUniqueAttributes = (attributesString) => {
    const attributes = attributesString
      .split(";")
      .map((attr) => {
        const [key, value] = attr.split(":");
        return { key: key.trim(), value: value.trim() };
      })
      .filter((attr) => attr.key && attr.value);

    const uniqueAttributes = attributes.filter(
      (attr, index, self) =>
        index ===
        self.findIndex((a) => a.key === attr.key && a.value === attr.value)
    );

    return uniqueAttributes;
  };

  // Helper function to get unique size-price pairs
  const getUniqueSizePricePairs = (sizePricePairs) => {
    if (!Array.isArray(sizePricePairs)) return [];

    const uniquePairs = sizePricePairs.filter(
      (pair, index, self) =>
        index ===
        self.findIndex(
          (p) =>
            p.size.trim() === pair.size.trim() &&
            p.price === pair.price &&
            p.quantity === pair.quantity // Include quantity in uniqueness check
        )
    );

    return uniquePairs;
  };

  const parseAttributes = (attributesString) => {
    return attributesString
      .split(";")
      .map((attr) => {
        const [key, value] = attr.split(":");
        return { key: key.trim(), value: value.trim() };
      })
      .filter((attr) => attr.key && attr.value);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        "https://backend.gulabidecor.com/api/categories/getAllCategories"
      );
      setCategories(response.data.categoriesWithImages || []);
    } catch (err) {
      console.error("Error fetching categories:", err.message);
    }
  };

  const handleEdit = (product) => {
    // Map category names to their IDs for pre-selection
    console.log("Product:", product);
    const selectedCategoryIds = categories
      .filter((cat) => product.category_names_array?.split(",").includes(cat.name))
      .map((cat) => cat.category_id);

    setCurrentProduct(product);
    setAttributes(product.attributes || []);
    setSizePricePairs(product.sizePricePairs || []);
    setProductProperties(
      Object.entries(product.productProperties || {}).map(([key, value]) => ({
        key,
        value,
      }))
    );

    // Calculate total quantity from sizes
    const calculatedTotalQuantity = product.sizePricePairs.reduce(
      (total, pair) => total + (parseInt(pair.quantity) || 0),
      0
    );

    setTotalQuantity(calculatedTotalQuantity);

    // Important: Set form values AFTER calculating the total
    form.setFieldsValue({
      name: product.name,
      description: product.description,
      price: product.price,
      discount: product.discount,
      category: selectedCategoryIds, // Pre-select multiple categories
      quantity: calculatedTotalQuantity, // Set total quantity based on sizes
    });

    setImageFiles(product.images);
    setIsEditing(true);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImageFiles = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setImageFiles((prevImages) => [...prevImages, ...newImageFiles]);
  };

  const handleImageRemove = (index) => {
    const removedImage = imageFiles[index];

    // If the image is an existing one (URL), add it to deletedImages
    if (!removedImage.file) {
      const deletedImagePath = removedImage; // URL of the deleted image
      setDeletedImages((prevDeleted) => [...prevDeleted, deletedImagePath]);
    }

    setImageFiles((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleAddAttribute = () => {
    setAttributes([...attributes, { key: "", value: "" }]);
  };

  const handleRemoveAttribute = (index) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleAttributeChange = (index, field, value) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index][field] = value;
    setAttributes(updatedAttributes);
  };

  const handleAddSizePricePair = () => {
    const updatedPairs = [...sizePricePairs, { size: "", price: "", quantity: "" }];
    setSizePricePairs(updatedPairs);
  };

  const handleRemoveSizePricePair = (index) => {
    const updatedPairs = sizePricePairs.filter((_, i) => i !== index);
    setSizePricePairs(updatedPairs);
  };

  const handleSizePriceChange = (index, field, value) => {
    const updatedPairs = [...sizePricePairs];
    updatedPairs[index][field] = value;
    setSizePricePairs(updatedPairs);

    // Note: We don't need to update the total quantity here anymore
    // as it's handled by the useEffect
  };

  const handleAddProductProperty = () => {
    setProductProperties([...productProperties, { key: "", value: "" }]);
  };

  const handleRemoveProductProperty = (index) => {
    setProductProperties(productProperties.filter((_, i) => i !== index));
  };

  const handleProductPropertyChange = (index, field, value) => {
    const updatedProperties = [...productProperties];
    updatedProperties[index][field] = value;
    setProductProperties(updatedProperties);
  };

  const handleEditSubmit = async () => {
    try {
      const updatedProduct = form.getFieldsValue();

      // Create a FormData object for sending data as multipart/form-data
      const formData = new FormData();
      // Append required fields
      formData.append("productId", currentProduct.key); // Use the product ID
      formData.append("name", updatedProduct.name);
      formData.append("description", updatedProduct.description);
      formData.append("price", updatedProduct.price);
      formData.append("categories", JSON.stringify(updatedProduct.category)); // Multiple categories
      formData.append("discount", updatedProduct.discount || 0);
      formData.append("quantity", updatedProduct.quantity); // Append quantity

      // Append attributes as a JSON string
      formData.append("attributes", JSON.stringify(attributes));

      // Append size-price pairs as a JSON string
      formData.append("sizes", JSON.stringify(sizePricePairs));

      // Append product properties (additional descriptions) as a JSON string
      formData.append("productDescriptions", JSON.stringify(productProperties));

      // Get paths of existing images
      const existingImages = imageFiles
        .filter((image) => !image.file)
        .map((image) => image); // Exclude new image files

      // Track deleted images (existing images that were removed)
      const deletedImages = currentProduct.images.filter(
        (image) => !imageFiles.find((file) => file === image)
      );

      // Send deleted image paths to backend
      formData.append("deletedImages", JSON.stringify(deletedImages));

      // Append new images
      imageFiles.forEach((image) => {
        if (image.file) {
          formData.append("images", image.file); // New images
        } else {
          formData.append("images", image); // Existing images (URLs)
        }
      });

      // Send the update request to the backend
      await axios.put(
        `https://backend.gulabidecor.com/api/products/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("Product updated successfully.");
      setIsEditing(false);
      fetchProducts(); // Refresh the product list
    } catch (err) {
      console.error("Error updating product:", err.message);
      message.error("Failed to update product.");
    }
  };

  const handleDelete = async (productId) => {
    try {
      console.log(productId);
      // Make sure the productId is sent correctly in the URL
      await axios.delete(
        `https://backend.gulabidecor.com/api/products/${productId}` // Ensure 'productId' is passed as URL parameter
      );
      message.success("Product deleted successfully.");
      fetchProducts(); // Refresh the product list after deletion
      window.location.reload(); // Reload the page after successful deletion
    } catch (err) {
      console.error("Error deleting product:", err.message);
      message.error("Failed to delete product.");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentProduct(null);
  };

  const fetchReviews = async (productId) => {
    try {
      const response = await axios.get(
        `https://backend.gulabidecor.com/api/reviews/${productId}`
      );
      setReviews(response.data);
      setIsReviewModalVisible(true);
    } catch (err) {
      console.error("Error fetching reviews:", err.message);
    }
  };

  const handleReviewEdit = (review) => {
    setCurrentReview(review);
    reviewForm.setFieldsValue({
      rating: review.rating,
      comment: review.comment,
    });
    setIsEditingReview(true);
  };

  const handleReviewDelete = async (reviewId) => {
    try {
      await axios.delete(
        `https://backend.gulabidecor.com/api/reviews/deleteReviews/${reviewId}`
      );
      message.success("Review deleted successfully.");
      fetchReviews(currentReview.product_id); // Refresh the reviews list
    } catch (err) {
      console.error("Error deleting review:", err.message);
      message.error("Failed to delete review.");
    }
  };

  const handleReviewSubmit = async () => {
    try {
      const updatedReview = reviewForm.getFieldsValue();
      console.log(currentReview.review_id);
      await axios.put(
        `https://backend.gulabidecor.com/api/reviews/updateReview/${currentReview.review_id}`,
        updatedReview
      );
      message.success("Review updated successfully.");
      setIsEditingReview(false);
      fetchReviews(currentReview.product_id); // Refresh the reviews list
    } catch (err) {
      console.error("Error updating review:", err.message);
      message.error("Failed to update review.");
    }
  };

  // Excel Export Function
  const exportToExcel = () => {
    if (filteredProducts.length === 0) {
      message.warning("No products to export.");
      return;
    }

    setExportLoading(true);
    try {
      // Find maximum number of images across all products
      const maxImages = Math.max(
        ...filteredProducts.map(product => (product.images || []).length),
        0
      );

      // Prepare data for export
      const exportData = [];

      // Build header row with dynamic image columns
      const headerRow = [
        "Product ID",
        "Product Name",
        "Description",
        "Price",
        "Discount (%)",
        "Total Quantity",
        "Category",
        "Categories (All)",
        "Size-Price-Quantity Pairs",
        "Attributes",
        "Product Properties",
        "Image Count"
      ];

      // Add separate columns for each image
      for (let i = 1; i <= maxImages; i++) {
        headerRow.push(`Image ${i}`);
      }

      exportData.push(headerRow);

      // Process each product
      filteredProducts.forEach((product) => {
        // Format size-price-quantity pairs
        const sizePricePairs = product.sizePricePairs || [];
        const sizePricePairsStr = sizePricePairs.length > 0
          ? sizePricePairs.map(pair =>
            `Size: ${pair.size || 'N/A'}, Price: ₹${parseFloat(pair.price || 0).toFixed(2)}, Quantity: ${pair.quantity || 0}`
          ).join("; ")
          : "N/A";

        // Format attributes
        const attributes = product.attributes || [];
        const attributesStr = attributes.length > 0
          ? attributes.map(attr => `${attr.key || 'N/A'}: ${attr.value || 'N/A'}`).join("; ")
          : "N/A";

        // Format product properties
        const productProperties = product.productProperties || {};
        const propertiesStr = Object.keys(productProperties).length > 0
          ? Object.entries(productProperties).map(([key, value]) => `${key}: ${value}`).join("; ")
          : "N/A";

        // Format categories
        const categoryStr = product.category || "N/A";
        const allCategoriesStr = product.category_names_array
          ? product.category_names_array.split(",").join("; ")
          : categoryStr;

        // Format images - separate URLs
        const images = product.images || [];
        const imageUrls = images.map(img => `https://backend.gulabidecor.com${img}`);

        // Build product row
        const productRow = [
          product.key || "N/A",
          product.name || "N/A",
          product.description || "N/A",
          `₹${parseFloat(product.price || 0).toFixed(2)}`,
          product.discount ? `${product.discount}%` : "0%",
          product.quantity || 0,
          categoryStr,
          allCategoriesStr,
          sizePricePairsStr,
          attributesStr,
          propertiesStr,
          images.length
        ];

        // Add image URLs in separate columns
        for (let i = 0; i < maxImages; i++) {
          productRow.push(imageUrls[i] || "");
        }

        exportData.push(productRow);
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 12 },  // Product ID
        { wch: 30 },  // Product Name
        { wch: 50 },  // Description
        { wch: 12 },  // Price
        { wch: 12 },  // Discount
        { wch: 12 },  // Total Quantity
        { wch: 20 },  // Category
        { wch: 30 },  // Categories (All)
        { wch: 60 },  // Size-Price-Quantity Pairs
        { wch: 40 },  // Attributes
        { wch: 40 },  // Product Properties
        { wch: 12 }   // Image Count
      ];

      // Add column widths for image columns
      for (let i = 0; i < maxImages; i++) {
        colWidths.push({ wch: 60 }); // Image URL columns
      }

      ws['!cols'] = colWidths;

      // Freeze header row for better navigation
      ws['!freeze'] = { x: 0, y: 1 };

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Products");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `Products_Export_${timestamp}.xlsx`;

      // Write file
      XLSX.writeFile(wb, filename);

      message.success(`Successfully exported ${filteredProducts.length} product(s) to Excel.`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("Failed to export products to Excel. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // CSV Export Function
  const exportToCSV = () => {
    if (filteredProducts.length === 0) {
      message.warning("No products to export.");
      return;
    }

    try {
      // Prepare CSV data
      let csvContent = "Product ID,Product Name,Description,Price,Discount (%),Total Quantity,Category,Categories (All),Size-Price-Quantity Pairs,Attributes,Product Properties,Image Count\n";

      filteredProducts.forEach((product) => {
        // Format size-price-quantity pairs
        const sizePricePairs = product.sizePricePairs || [];
        const sizePricePairsStr = sizePricePairs.length > 0
          ? sizePricePairs.map(pair =>
            `Size: ${pair.size || 'N/A'}, Price: ₹${parseFloat(pair.price || 0).toFixed(2)}, Quantity: ${pair.quantity || 0}`
          ).join("; ")
          : "N/A";

        // Format attributes
        const attributes = product.attributes || [];
        const attributesStr = attributes.length > 0
          ? attributes.map(attr => `${attr.key || 'N/A'}: ${attr.value || 'N/A'}`).join("; ")
          : "N/A";

        // Format product properties
        const productProperties = product.productProperties || {};
        const propertiesStr = Object.keys(productProperties).length > 0
          ? Object.entries(productProperties).map(([key, value]) => `${key}: ${value}`).join("; ")
          : "N/A";

        // Format categories
        const categoryStr = product.category || "N/A";
        const allCategoriesStr = product.category_names_array
          ? product.category_names_array.split(",").join("; ")
          : categoryStr;

        const images = product.images || [];

        const row = [
          product.key || "N/A",
          `"${(product.name || "N/A").replace(/"/g, '""')}"`,
          `"${(product.description || "N/A").replace(/"/g, '""')}"`,
          `₹${parseFloat(product.price || 0).toFixed(2)}`,
          product.discount ? `${product.discount}%` : "0%",
          product.quantity || 0,
          `"${categoryStr.replace(/"/g, '""')}"`,
          `"${allCategoriesStr.replace(/"/g, '""')}"`,
          `"${sizePricePairsStr.replace(/"/g, '""')}"`,
          `"${attributesStr.replace(/"/g, '""')}"`,
          `"${propertiesStr.replace(/"/g, '""')}"`,
          images.length
        ].join(",");

        csvContent += row + "\n";
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute("download", `Products_Export_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${filteredProducts.length} product(s) to CSV.`);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      message.error("Failed to export products to CSV. Please try again.");
    }
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "images",
      render: (images) =>
        images.length > 0 ? (
          <img
            src={`https://backend.gulabidecor.com${images[0]}`}
            alt="product"
            style={{
              width: "50px",
              height: "50px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        ) : (
          "No Image"
        ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (name) => (
        <a
          href={`http://gulabidecor.com/product/${name
            .replace(/\s+/g, " ")
            .toLowerCase()}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "underline", color: "black" }}
        >
          {name}
        </a>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
    },
    {
      title: "Discount",
      dataIndex: "discount",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      render: (quantity) => quantity || 0,
    },
    {
      title: "Category",
      dataIndex: "category",
    },
    {
      title: "Reviews",
      dataIndex: "reviews",
      render: (_, record) => (
        <Button onClick={() => fetchReviews(record.key)}>View Reviews</Button>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <>
          <BiEdit className="action-icon" onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.key)} // product key (productId) is passed here
            okText="Yes"
            cancelText="No"
          >
            <AiFillDelete className="action-icon text-danger" />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}>
        <h3 className="mb-0 title">Products</h3>
        <Space>
          <Button
            type="default"
            icon={<FileExcelOutlined />}
            onClick={exportToExcel}
            disabled={filteredProducts.length === 0}
            loading={exportLoading}
            style={{
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              color: '#fff'
            }}
          >
            Export to Excel
          </Button>
          <Button
            type="default"
            onClick={exportToCSV}
            disabled={filteredProducts.length === 0}
            style={{
              backgroundColor: '#1890ff',
              borderColor: '#1890ff',
              color: '#fff'
            }}
          >
            Export to CSV
          </Button>
        </Space>
      </div>
      <Input.Search
        placeholder="Search by product name"
        allowClear
        enterButton
        style={{ marginBottom: 16, maxWidth: 300 }}
        onChange={(e) => setSearchTerm(e.target.value)}
        value={searchTerm}
      />
      <Table columns={columns} dataSource={filteredProducts} />

      <Modal
        title="Edit Product"
        open={isEditing}
        onOk={handleEditSubmit}
        onCancel={handleCancel}
        width={800} // Make the modal wider for better display
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="price" label="Price" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="discount" label="Discount">
            <Input type="number" />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please select at least one category" }]}
          >
            <Select
              mode="multiple" // Allow multiple selection
              placeholder="Select categories"
              allowClear
            >
              {categories.map((category) => (
                <Select.Option
                  key={category.category_id}
                  value={category.category_id}
                >
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Updated Quantity display */}
          <Form.Item name="quantity" label="Total Quantity">
            <Input
              type="number"
              readOnly
              value={totalQuantity}
              style={{ backgroundColor: '#f5f5f5' }} // Grey background to indicate read-only
            />
          </Form.Item>

          {/* Product Attributes Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Product Attributes</label>
            {attributes.map((attribute, index) => (
              <div key={index} className="d-flex gap-2 mb-3">
                <Select
                  placeholder="Select Attribute"
                  value={attribute.key}
                  onChange={(value) => handleAttributeKeyChange(index, value)}
                  style={{ width: "45%" }}
                >
                  {attributeNames.map((attr) => (
                    <Select.Option key={attr.attribute_id} value={attr.name}>
                      {attr.name}
                    </Select.Option>
                  ))}
                </Select>
                {attribute.isCustom ? (
                  <div className="d-flex gap-2" style={{ width: "45%" }}>
                    <Input
                      placeholder="Enter Custom Value"
                      value={attribute.value}
                      onChange={(e) => handleCustomValueChange(index, e.target.value)}
                    />
                    <Button
                      type="text"
                      danger
                      onClick={() => handleCustomValueToggle(index, false)}
                    >
                      ✕
                    </Button>
                  </div>
                ) : (
                  <Select
                    placeholder="Select or Add Value"
                    value={attribute.value}
                    onChange={(value) =>
                      value === "__custom"
                        ? handleCustomValueToggle(index, true)
                        : handleAttributeValueChange(index, value)
                    }
                    style={{ width: "45%" }}
                  >
                    {attributeValues[index]?.map((val, i) => (
                      <Select.Option key={i} value={val}>
                        {val}
                      </Select.Option>
                    ))}
                    <Select.Option value="__custom">Add Value</Select.Option>
                  </Select>
                )}
                <Button
                  type="danger"
                  onClick={() => handleRemoveAttribute(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={handleAddAttribute}>
              Add Attribute
            </Button>
          </div>

          {/* Size-Price Pairs Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Size-Price-Quantity Pairs</label>
            <div style={{ marginBottom: '10px', color: 'blue' }}>
              Note: Total Quantity will be calculated from the sum of all size quantities automatically
            </div>
            {sizePricePairs.map((pair, index) => (
              <div key={index} className="d-flex gap-2 mb-3">
                <Input
                  placeholder="Size"
                  value={pair.size}
                  onChange={(e) =>
                    handleSizePriceChange(index, "size", e.target.value)
                  }
                  style={{ width: '30%' }}
                />
                <Input
                  placeholder="Price"
                  value={pair.price}
                  onChange={(e) =>
                    handleSizePriceChange(index, "price", e.target.value)
                  }
                  style={{ width: '30%' }}
                />
                <Input
                  placeholder="Quantity"
                  value={pair.quantity}
                  onChange={(e) =>
                    handleSizePriceChange(index, "quantity", e.target.value)
                  }
                  style={{ width: '30%' }}
                />
                <Button
                  type="danger"
                  onClick={() => handleRemoveSizePricePair(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={handleAddSizePricePair}>
              Add Size-Price-Quantity Pair
            </Button>
          </div>

          {/* Product Properties Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Product Properties</label>
            {productProperties.map((property, index) => (
              <div key={index} className="d-flex gap-2 mb-3">
                <Input
                  placeholder="Property"
                  value={property.key}
                  onChange={(e) =>
                    handleProductPropertyChange(index, "key", e.target.value)
                  }
                  style={{ width: '45%' }}
                />
                <Input
                  placeholder="Value"
                  value={property.value}
                  onChange={(e) =>
                    handleProductPropertyChange(index, "value", e.target.value)
                  }
                  style={{ width: '45%' }}
                />
                <Button
                  type="danger"
                  onClick={() => handleRemoveProductProperty(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={handleAddProductProperty}>
              Add Property
            </Button>
          </div>

          {/* Image Upload Section */}
          <div>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Product Images</label>
            <Button onClick={() => fileInputRef.current.click()}>
              <AiOutlineUpload /> Upload Images
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <div
              className="image-preview-container"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "10px",
                marginTop: "15px"
              }}
            >
              {imageFiles.map((image, index) => (
                <div
                  key={index}
                  className="image-preview"
                  style={{ textAlign: "center" }}
                >
                  <img
                    src={image.url || `https://backend.gulabidecor.com${image}`}
                    alt="Product"
                    className="img-thumbnail"
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                      marginBottom: "10px",
                    }}
                  />
                  <Button danger onClick={() => handleImageRemove(index)}>
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Product Reviews"
        visible={isReviewModalVisible}
        onCancel={() => setIsReviewModalVisible(false)}
        footer={null}
      >
        <Table
          dataSource={reviews}
          columns={[
            {
              title: "Review ID",
              dataIndex: "review_id",
            },
            {
              title: "User ID",
              dataIndex: "user_id",
            },
            {
              title: "Rating",
              dataIndex: "rating",
            },
            {
              title: "Comment",
              dataIndex: "comment",
            },
            {
              title: "Created At",
              dataIndex: "created_at",
            },
            {
              title: "Actions",
              render: (_, record) => (
                <>
                  <BiEdit
                    className="action-icon"
                    onClick={() => handleReviewEdit(record)}
                  />
                  <Popconfirm
                    title="Are you sure you want to delete this review?"
                    onConfirm={() => handleReviewDelete(record.review_id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <AiFillDelete className="action-icon text-danger" />
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title="Edit Review"
        visible={isEditingReview}
        onOk={handleReviewSubmit}
        onCancel={() => setIsEditingReview(false)}
      >
        <Form form={reviewForm} layout="vertical">
          <Form.Item name="rating" label="Rating" rules={[{ required: true }]}>
            <Input type="number" min={1} max={5} />
          </Form.Item>
          <Form.Item
            name="comment"
            label="Comment"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Productlist;